import 'jquery-validation';
import 'jquery-validation/dist/additional-methods';
import AbstractValidatorOptions from './abstract-validator-options';
import './custom-validation-rules';
import './validator-messages';
import Localization from '../../other/localization';

/**
 * Abstract validator module.
 *
 * @author Kovács Tamás
 * @param {string} formId - The given form id
 * @returns Abstract validator module
 */
function AbstractValidator(formId) {
    const form = $(`#${formId}`);

    const localization = new Localization();

    /**
     * Reset the form, clear the validated fields feedback.
     */
    function resetForm() {
        form.find('.is-invalid').removeClass('is-invalid');
    }

    /**
     * Handle the successful request.
     *
     * @param {object} response - The response from the backend
     * @param {AbstractValidatorOptions=} options The validator options
     */
    function handleSuccess(response, options = AbstractValidatorOptions) {
        form.trigger('success', [response]);
        if (options.errorContainer !== null) {
            options.errorContainer.empty().hide();
        }

        if (response.redirect) {
            window.location.assign(
                response.redirect,
            );
        }

        if (response.message) {
            if (response.error) {
                if (options.showToastMessagesOnError && options.showToastMessages) {
                    window.toastr.error(
                        response.message,
                        response.title ?? '',
                    );
                }
                else if (options.errorContainer !== null) {
                    options.errorContainer.html(response.message).show();
                }
            }
            else {
                if (options.showToastMessagesOnSuccess && options.showToastMessages) {
                    window.toastr.success(
                        response.message,
                    );
                }
                resetForm();
            }
        }
    }

    /**
     * Creates red frame and red text feedback for the form elements
     */
    function createFormElementFeedback(key, message) {
        if (!$(`#${key}-error`, form).length) {
            const obj = $(`input[name="${key}"]`);
            $(`<label id="${key}-error" class="error" for="${key}"></label>`)
            .insertAfter(
                (
                    obj.length > 0
                    ? obj
                    : $(`.${key}-error-holder`, form)
                ),
                form,
            );
        }

        const feedback = $(`#${key}-error`, form);
        feedback.text(message);
        feedback.parent().addClass('is-invalid');

        if (!feedback.is('visible')) {
            feedback.show();
        }

        let offsetTop = 0;
        if (feedback.offset()) {
            offsetTop = feedback.offset().top;
        }

        return offsetTop;
    }

    /**
     * Handle the failed request.
     *
     * @param {object} errors - The validation errors from the backend
     * @param {AbstractValidatorOptions=} options The validator options
     */
    function handleErrors(errors, options = AbstractValidatorOptions) {
        let firstErrorPosition = 9999;

        if (options.errorContainer !== null) {
            options.errorContainer.empty().show();
        }

        $(errors)
        .each(
            (index, error) => {
                Object
                .entries(error)
                .forEach(
                    ([key, message]) => {
                        if (options.errorContainer === null) {
                            const elementPos = createFormElementFeedback(key, message);
                            if (elementPos < firstErrorPosition) {
                                firstErrorPosition = elementPos;
                            }
                        }
                        else {
                            options.errorContainer.append(message);
                        }

                        if (options.showToastMessagesOnError && options.showToastMessages) {
                            window.toastr.error(
                                message,
                                localization.getMessage('error'),
                            );
                        }
                    },
                );
            },
        );

        if (options.scrollToFirstError
            && firstErrorPosition < 9999
            && firstErrorPosition > 0
        ) {
            $('html, body').animate(
                {
                    scrollTop: firstErrorPosition,
                },
                options.scrollToErrorDelay,
            );
        }

        form.trigger('error', [errors]);
    }

    /**
     * Toggle submit button loading state, add 'button-loading' class.
     */
    function toggleSubmitButtonLoadingState() {
        const submitButton = form.find('button[type="submit"]');
        const disabled = !!submitButton.attr('disabled');

        submitButton.attr('disabled', !disabled);

        if (!disabled) {
            submitButton.addClass('button-loading');
        }
        else if (submitButton.hasClass('button-loading')) {
            submitButton.removeClass('button-loading');
        }
    }

    /**
     * Validate the form.
     *
     * @param {object} form The given form
     * @param {object} rules Object with the given validation rules
     * @param {AbstractValidatorOptions=} options The validator options
     * @returns Validator object
     */
    function init(rules, options = AbstractValidatorOptions()) {
        return (
            form.validate({
                submitHandler() {
                    toggleSubmitButtonLoadingState();
                    form.trigger('submitStart');
                    $.ajax({
                        method: form.attr('method'),
                        url: form.attr('action'),
                        data: form.serializeArray(),
                        type: 'json',
                        statusCode: {
                            500: () => {
                                const errorMessage = {
                                    'unexpected-error': localization.getMessage('unexpected_error'),
                                };
                                handleErrors(errorMessage, options);
                            },
                        },
                    })
                    .done((response) => {
                        handleSuccess(response, options);
                    })
                    .fail(({ responseJSON }) => {
                        if (typeof responseJSON.errors !== 'undefined') {
                            handleErrors(responseJSON.errors, options);
                        }
                    })
                    .always(() => {
                        toggleSubmitButtonLoadingState();
                        form.trigger('submitFinished');
                    });
                },
                invalidHandler() {
                    // For frontend validation error
                    form.trigger('invalidForm');
                },
                rules,
                ...options,
            })
        );
    }

    return {
        init,
        form,
        resetForm,
    };
}

export default AbstractValidator;
